import React, { useEffect, useRef, useState } from "react";
import {
  Radar,
  Link2,
  Brain,
  UserCheck,
  ShieldAlert,
  Activity,
  Sparkles,
  ChevronRight,
  Check,
  X as XIcon,
  ArrowUpRight,
} from "lucide-react";

/**
 * CardFraudAgentPanel — shared AI workflow panel.
 *
 * Renders the card-fraud agent pipeline (Detected → Enriching → Analyzing
 * → Officer review → Actioning → Monitoring) + spotlight case detail +
 * human-in-the-loop review card + case queue. Auto-advance simulation
 * moves random cases forward every ~7s (jittered). Approve/reject/
 * escalate on the officer card mutate the case list.
 *
 * Visual language: MAIN SKIN — cobalt gradient hero header, shadow
 * cards, main-branch blue palette. Consistent with landing/
 * ReportingDashboardHome so it drops into the pre-facelift dashboard
 * without visual friction. The facelift dashboard imports the same
 * component (no separate skin per direction "main-skin first").
 */

// ─── Stage config ───────────────────────────────────────────────────────
const STAGES = [
  { key: "detected",   label: "Detected",       Icon: Radar },
  { key: "enriching",  label: "Enriching",      Icon: Link2 },
  { key: "analyzing",  label: "Analyzing",      Icon: Brain },
  { key: "review",     label: "Officer review", Icon: UserCheck, hitl: true },
  { key: "actioning",  label: "Actioning",      Icon: ShieldAlert },
  { key: "monitoring", label: "Monitoring",     Icon: Activity, terminal: true },
];

const STAGE_ORDER = STAGES.map((s) => s.key);

// ─── Mock case data ─────────────────────────────────────────────────────
// Every case below is grounded in a real rule from
// scoring/card_risk_rules.yaml. Triggers, evidence values, weights,
// score composition, and the resulting stage/action all map to what the
// production scorer would actually do. No made-up jargon.
//
// Score composition uses the rule weights as-is; total is clamped to
// [0, 1]. thresholds: review=0.50, block=0.80.
const INITIAL_CASES = [
  {
    // Real rules fired: "High Confidence Card Testing" (0.65), "Velocity 30m"
    // (0.30), "Visitor BIN Fanout Suspicious" (0.20). Composite ~0.94 →
    // above thresholds.block (0.80). Routed to Officer Review because the
    // proposed action (hard-blacklist 5 linked visitors) trips the 4-eyes
    // rule (> 3 users). Evidence values are shown in plain English but map
    // directly to the params in card_risk_rules.yaml.
    id: "CF-4821",
    trigger: "Coordinated card-testing spike",
    stage: "review",
    entity: "12 linked visitors · anchored on vinny.tester99@protonmail.com",
    officer: { role: "Senior Fraud Analyst" },
    confidence: 95,
    riskTier: "High",
    timeInStage: "2h 14m",
    evidence: [
      { label: "Linked visitors identified",         value: "12" },
      { label: "Merchants targeted",                 value: "Both sponsored merchants" },
      { label: "Combined attempts · per hour",       value: "380" },
      { label: "Decline rate across the group",      value: "87%" },
      { label: "Shared device fingerprint clusters", value: "4" },
      { label: "Distinct card BINs tried",           value: "8" },
    ],
    proposedAction:
      "Hard-blacklist all 12 visitors across every sponsored merchant · add the 4 shared device fingerprints to the cross-merchant blocklist so future signups on those devices are blocked at the sponsored book level · turn on the extra password check (Force 3DS) for ~50 device-adjacent visitors we haven't caught yet.",
    fourEyes: true,
    reasoning: [
      { t: "09:12:04", note: "Card-testing behaviour detected on 12 linked visitors — combined 380 attempts per hour on small deposits, 87% declined. Your threshold is 3 attempts/hour per visitor on any amount under $25; this group is well over on every one of them." },
      { t: "09:12:07", note: "Ring signal — the 12 visitors share 4 device fingerprint clusters. They aren't independent; they're one coordinated group operating across both sponsored merchants." },
      { t: "09:12:11", note: "Card-BIN fanout across the group — 8 distinct BINs tried in the last hour. That matches the sequential-enumeration pattern of a bot working through a stolen card list." },
      { t: "09:12:14", note: "Combined risk score is 0.95 — well above your Block threshold of 0.80." },
      { t: "09:12:15", note: "The proposed action is bank-level: hard-blacklist 12 + add 4 device fingerprints to the cross-merchant blocklist + Force-3DS on ~50 more. That affects 62+ visitors total, well above your 3-user 4-eyes threshold." },
      { t: "09:12:15", note: "Sent to Officer Review for co-sign per your 4-eyes rule." },
    ],
    triggeredLever: "4-eyes rule",
  },
  {
    // Portfolio-wide velocity anomaly — transactions across both sponsored
    // merchants running 3x baseline in the last hour. Agent is correlating
    // visitor patterns to distinguish an attack from organic growth.
    id: "CF-4820",
    trigger: "Portfolio-wide velocity anomaly",
    stage: "analyzing",
    entity: "Both sponsored merchants · last hour",
    confidence: 65,
    riskTier: "Medium",
    timeInStage: "9m",
    evidence: [
      { label: "Transactions · last hour",             value: "4,280" },
      { label: "Baseline hourly rate",                 value: "1,400" },
      { label: "Merchant A share of the spike",        value: "42%" },
      { label: "Merchant B share of the spike",        value: "58%" },
      { label: "Unique visitors driving the spike",    value: "340" },
    ],
    proposedAction:
      "Correlate visitor patterns across both merchants to distinguish attack from organic growth. If ring signals emerge, auto-apply portfolio-level compensating controls; if organic, no action.",
    fourEyes: false,
    reasoning: [
      { t: "10:47:22", note: "Portfolio-level velocity spike detected — 4,280 transactions across both sponsored merchants in the last hour vs a baseline of 1,400/hour. That's 3x normal." },
      { t: "10:47:25", note: "The spike is split roughly 42/58 across Merchants A and B — coordinated cross-merchant behaviour rather than a single-merchant surge." },
      { t: "10:47:27", note: "340 unique visitors are driving the spike. Enriching each cluster for shared device fingerprints and BIN overlap to classify attack vs organic." },
      { t: "10:47:29", note: "Combined risk score so far is 0.65. Waiting on correlation results before deciding whether to auto-apply compensating controls or route to Officer Review." },
    ],
  },
  {
    // Emerging cross-BIN attack signal — three of the bank's High-risk BINs
    // are showing coordinated activity from 42 visitors in the last 4 hrs.
    // Agent is enriching the device fingerprint graph to see if these
    // visitors share infrastructure (which would confirm a coordinated
    // attack vs coincidental overlap).
    id: "CF-4819",
    trigger: "Emerging cross-BIN attack signal",
    stage: "enriching",
    entity: "3 High-risk BINs · 42 visitors · 4 hrs",
    confidence: 62,
    riskTier: "Medium",
    timeInStage: "3m",
    evidence: [
      { label: "BINs affected",                       value: "421761, 533248, 485956" },
      { label: "Visitors touching flagged BINs",      value: "42" },
      { label: "Time window",                         value: "4 hours" },
      { label: "Cross-merchant footprint",            value: "Both sponsored merchants" },
    ],
    proposedAction: "Enriching the device fingerprint graph to see if these 42 visitors share infrastructure. If confirmed coordinated, auto-apply Force 3DS on the entire BIN range and slow down the affected sessions.",
    fourEyes: false,
    reasoning: [
      { t: "10:53:11", note: "Three of your High-risk BINs (421761, 533248, 485956) are all firing at once — 42 visitors touching them in the last 4 hours. Individually each BIN is expected to show some activity; simultaneously is not." },
      { t: "10:53:13", note: "Cross-merchant footprint — the 42 visitors are hitting both sponsored merchants, not concentrated on one. That's a coordinated-attack signature." },
      { t: "10:53:15", note: "Combined risk score so far is 0.62. Enriching device fingerprints across the 42 visitors — if shared infrastructure is confirmed, agent will auto-apply Force 3DS on the affected BIN range." },
    ],
  },
  {
    // BIN-attack wave · auto-mitigated. High-risk BIN 421761 saw 34
    // visitors running card-testing patterns in the last hour. Agent
    // auto-applied Force 3DS on all 34 + Cumulative Abuse slowdown per
    // bank policy (BIN is on the force_3ds allow-list, so this is
    // within the agent's autonomy envelope).
    id: "CF-4818",
    trigger: "BIN-attack wave · auto-mitigated",
    stage: "actioning",
    entity: "BIN 421761 · 34 visitors",
    confidence: 88,
    riskTier: "High",
    timeInStage: "just now",
    evidence: [
      { label: "BIN affected",                     value: "421761 (High-risk BIN)" },
      { label: "Visitors flagged in the wave",     value: "34" },
      { label: "Card-test attempts · last hour",   value: "210" },
      { label: "Force 3DS auto-applied to",        value: "34 visitors" },
      { label: "Slowdown auto-applied to",         value: "34 visitors" },
    ],
    proposedAction: "Auto-mitigated. All 34 visitors on BIN 421761 are on Force 3DS and deposit slowdown. Attack wave contained within the agent's autonomy envelope; no officer signoff required.",
    fourEyes: false,
    reasoning: [
      { t: "10:55:02", note: "High-risk BIN wave detected — 34 visitors on BIN 421761 running card-testing patterns simultaneously." },
      { t: "10:55:03", note: "210 card-test attempts in the last hour on this BIN — well above the noise floor for a healthy BIN." },
      { t: "10:55:04", note: "BIN 421761 is on your Force-3DS allow-list. Auto-applied Force 3DS to all 34 visitors + Cumulative Abuse slowdown on their sessions." },
      { t: "10:55:05", note: "Combined risk score is 0.88 — above your Block threshold. Wave contained within the agent's autonomy envelope; no officer signoff required." },
    ],
  },
  {
    // Cross-merchant device blocklist · 24h observation window. 6 device
    // fingerprint clusters were added to the sponsored-book blocklist
    // yesterday after confirmed fraud on Merchant A. Agent is now
    // watching for rotation attempts (new signups on those devices being
    // auto-rejected at the door).
    id: "CF-4817",
    trigger: "Cross-merchant blocklist · 24h watch",
    stage: "monitoring",
    entity: "6 device fingerprint clusters · sponsored book",
    confidence: 30,
    riskTier: "Low",
    timeInStage: "6h",
    evidence: [
      { label: "Device clusters on blocklist",       value: "6 (added yesterday)" },
      { label: "Rotation attempts caught at signup", value: "0" },
      { label: "Hours remaining in watch window",    value: "18 of 24" },
      { label: "Merchants covered",                  value: "Both sponsored merchants" },
    ],
    proposedAction: "Watching the 6 device clusters for rotation attempts. If clean at 24h, the watch closes. If new signups attempt on any of the fingerprints, the agent auto-rejects at the door and re-opens the case.",
    fourEyes: false,
    reasoning: [],
  },
  {
    // Merchant B chargeback ratio surge — portfolio-level VAMP signal
    // just tripped. Merchant B's 24h chargeback ratio jumped from 55 bps
    // to 82 bps (over the Visa 50 bps standard). Agent is starting to
    // investigate which user cluster is driving the surge.
    id: "CF-4816",
    trigger: "Merchant B chargeback ratio surge",
    stage: "detected",
    entity: "Merchant B · portfolio VAMP signal",
    confidence: 0,
    riskTier: "Medium",
    timeInStage: "42s",
    evidence: [
      { label: "Merchant B chargeback ratio · 24h",  value: "82 bps (was 55)" },
      { label: "Visa VAMP standard",                 value: "50 bps" },
      { label: "New chargebacks · last 24 hrs",      value: "18" },
      { label: "Under investigation",                value: "Yes — identifying user cluster" },
    ],
    proposedAction: "Just detected. Agent is pulling the last-24h chargeback list on Merchant B to identify the user cluster driving the surge, then will size compensating controls.",
    fourEyes: false,
    reasoning: [
      { t: "10:58:32", note: "Merchant B's 24h chargeback ratio just jumped from 55 bps to 82 bps — above the 50 bps Visa VAMP standard. If sustained, the merchant would trip the compliance threshold on Visa's next scoring window." },
    ],
  },
  {
    // "FORCE-3DS User" flag currently set. Candidate for graduation to
    // "Whitelisted User" because 3DS pass rate is high and no chargebacks.
    // Officer signs off per 4-eyes on whitelist promotions.
    id: "CF-4815",
    trigger: "Weekly Force-3DS graduation cohort",
    stage: "review",
    entity: "47 users · this week's cohort",
    officer: { role: "BSA Compliance Officer" },
    confidence: 8,
    riskTier: "Low",
    timeInStage: "1h 08m",
    evidence: [
      { label: "Cohort size",                        value: "47 users" },
      { label: "Minimum days on Force 3DS",          value: "30" },
      { label: "Average 3DS pass rate · 30 days",    value: "96.5%" },
      { label: "Users with chargebacks",             value: "0 of 47" },
      { label: "Total spend · last 30 days · cohort", value: "$148,320" },
    ],
    proposedAction: "Bulk-remove the mandatory password check from all 47 users · move them to preferred-3DS routing (only check on higher-risk transactions) · add all 47 to the trusted list.",
    fourEyes: true,
    reasoning: [
      { t: "09:52:00", note: "All 47 users in this week's cohort meet your graduation policy — at least 30 days on the mandatory password check, 95% pass rate, and zero chargebacks." },
      { t: "09:52:01", note: "Bulk graduations are a positive-risk decision (we're removing friction). Per your 4-eyes rule, any batch action affecting more than 3 users needs officer co-sign." },
    ],
    triggeredLever: "4-eyes rule",
  },
  {
    // "Fraud cluster" (0.40) + "Visitor reuse High Risk" (0.60). Composite
    // 1.00 (clamped) → Block. Auto-actioned because the ring is a known
    // recurrence (prior blocklist case CF-4802 already sanctioned the
    // pattern); waiting for a human here is pure downside. The 4-eyes
    // rule is waived for recurrences of already-approved patterns.
    id: "CF-4814",
    trigger: "Known fraud ring · recurrence",
    stage: "actioning",
    entity: "4 linked identities · prior blocklist CF-4802",
    confidence: 100,
    riskTier: "High",
    timeInStage: "just now",
    evidence: [
      { label: "Linked to a known fraud ring",              value: "Yes" },
      { label: "Distinct users on this device · last 30d", value: "4" },
      { label: "Prior blocklist case",                     value: "CF-4802" },
    ],
    proposedAction: "Auto-blocked. Hard-blacklisted this visitor and 3 linked users; extended the cross-merchant blocklist so they can't rotate to a sibling merchant.",
    fourEyes: false,
    reasoning: [
      { t: "10:26:00", note: "Linked to a known fraud ring — this identity is tied to a fraud ring we blocked before (case CF-4802)." },
      { t: "10:26:01", note: "Device shared by too many users — this device has been used by 4 different users in the last 30 days. Your threshold is 3." },
      { t: "10:26:02", note: "Combined risk score is at the ceiling (1.00)." },
      { t: "10:26:03", note: "This pattern is a recurrence of an already-approved block (CF-4802). Your 4-eyes rule is waived for recurrences — waiting here is pure downside. Auto-executed." },
    ],
  },
];

// ─── Stage badge tone map — main-skin palette (matches Pill tones) ──────
const STAGE_STYLE = {
  detected:   { bg: "#EEF2FF", fg: "#3730A3", label: "Detected" },
  enriching:  { bg: "#EEF2FF", fg: "#3730A3", label: "Enriching" },
  analyzing:  { bg: "#EEF2FF", fg: "#3730A3", label: "Analyzing" },
  review:     { bg: "#EFF8FF", fg: "#175CD3", label: "Officer review" },
  actioning:  { bg: "#ECFDF3", fg: "#027A48", label: "Actioning" },
  monitoring: { bg: "#FFFAEB", fg: "#B54708", label: "Monitoring" },
};

const RISK_STYLE = {
  High:   { bg: "#FEF3F2", fg: "#B42318" },
  Medium: { bg: "#FFFAEB", fg: "#B54708" },
  Low:    { bg: "#ECFDF3", fg: "#027A48" },
};

// ─── Main component ─────────────────────────────────────────────────────
export default function CardFraudAgentPanel() {
  const [cases, setCases] = useState(INITIAL_CASES);
  const [filter, setFilter] = useState("review");
  const [spotlightId, setSpotlightId] = useState("CF-4821");
  const [pulseStage, setPulseStage] = useState(null);

  const spotlight = cases.find((c) => c.id === spotlightId) || cases[0];
  const stageCount = (k) => cases.filter((c) => c.stage === k).length;
  const filtered = cases.filter((c) => c.stage === filter);

  // Derived KPIs — tied to visible case list so labels literally explain themselves
  const totalCases = cases.length;
  const autoStages = new Set(["actioning", "monitoring"]);
  const autoHandled = cases.filter((c) => !c.fourEyes && autoStages.has(c.stage)).length;
  const waitingForReview = cases.filter((c) => c.stage === "review").length;
  const highRiskWaiting = cases.filter((c) => c.stage === "review" && c.fourEyes).length;
  const autoPct = totalCases > 0 ? Math.round((autoHandled / totalCases) * 100) : 0;

  const flash = (stageKey) => {
    setPulseStage(stageKey);
    setTimeout(() => setPulseStage(null), 900);
  };

  // Officer Review is reserved for BANK-LEVEL decisions — actions
  // affecting a group, a BIN range, a whole cohort, or an unknown
  // pattern the AI can't confidently classify. Single-user cases
  // (individual decline bursts, one card's AVS failures, one visitor's
  // compromised BIN) are auto-handled regardless of score band, because
  // sending every one to an officer would drown the queue.
  //
  //   fourEyes === true → Officer Review
  //   otherwise         → auto-decisioned (allow / force-3DS / block)
  const needsReview = (c) => c.fourEyes === true;

  const advance = (id, mode) => {
    const c = cases.find((cc) => cc.id === id);
    if (!c) return;
    let next = cases;
    if (mode === "approve") {
      const idx = STAGE_ORDER.indexOf(c.stage);
      const nextStage = STAGE_ORDER[Math.min(idx + 1, STAGE_ORDER.length - 1)];
      next = cases.map((cc) => (cc.id === id ? { ...cc, stage: nextStage, timeInStage: "just now" } : cc));
      flash(nextStage);
    } else if (mode === "reject") {
      next = cases.map((cc) =>
        cc.id === id
          ? { ...cc, stage: "monitoring", timeInStage: "just now", proposedAction: "Rejected by officer · no action taken · 24h monitoring" }
          : cc
      );
      flash("monitoring");
    } else if (mode === "escalate") {
      next = cases.map((cc) =>
        cc.id === id ? { ...cc, timeInStage: "just now", officer: { role: "Head of Compliance" } } : cc
      );
    }
    setCases(next);
  };

  // Auto-advance — every ~7s (jittered) advance a random non-HITL,
  // non-terminal case forward. If the next stage would be "review" and
  // the case doesn't genuinely need it (per needsReview), skip past
  // review so the queue stays clean.
  const casesRef = useRef(cases);
  casesRef.current = cases;
  useEffect(() => {
    const notAdvanceable = new Set(["review", "monitoring"]);
    let timer;
    const schedule = () => {
      const delay = 7000 + Math.random() * 3500;
      timer = setTimeout(() => {
        const advanceable = casesRef.current.filter((c) => !notAdvanceable.has(c.stage));
        if (advanceable.length > 0) {
          const picked = advanceable[Math.floor(Math.random() * advanceable.length)];
          const idx = STAGE_ORDER.indexOf(picked.stage);
          let nextStage = STAGE_ORDER[Math.min(idx + 1, STAGE_ORDER.length - 1)];
          // Skip Officer Review for cases that don't need it.
          if (nextStage === "review" && !needsReview(picked)) {
            nextStage = STAGE_ORDER[Math.min(idx + 2, STAGE_ORDER.length - 1)];
          }
          setCases((prev) =>
            prev.map((c) => (c.id === picked.id ? { ...c, stage: nextStage, timeInStage: "just now" } : c))
          );
          flash(nextStage);
        }
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Cobalt gradient hero header — matches ReportingDashboardHome */}
      <div
        style={{
          background: "linear-gradient(90deg, #0B3A6E 0%, #164E86 60%, #1F63A8 100%)",
          color: "white",
          borderRadius: 18,
          padding: "16px 18px",
          boxShadow: "0 18px 60px rgba(11,58,110,0.22)",
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 950, letterSpacing: 0.2, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={18} strokeWidth={2} />
            Card fraud AI · workflow
          </div>
          <div style={{ fontSize: 12, opacity: 0.92, marginTop: 4 }}>
            Live pipeline · click a stage to filter · click a case to inspect · approve on the officer card to advance.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Pill text="LIVE" tone="good" />
          <Pill text={`${totalCases} cases`} tone="info" />
          <Pill text="AI AGENT" tone="neutral" />
        </div>
      </div>

      {/* Metrics row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <StatCard
          label="Cases AI handled on its own"
          value={`${autoPct}%`}
          sub={`${autoHandled} of ${totalCases} cases · no human needed`}
        />
        <StatCard
          label="Cases waiting for you to review"
          value={waitingForReview}
          sub="Blocked pending human approval"
        />
        <StatCard
          label="High-risk cases in your queue"
          value={highRiskWaiting}
          sub="Need two officers to co-sign (4-eyes)"
        />
        <StatCard
          label="Fraud losses avoided · 7d"
          value="$12,840"
          sub="Across all sponsored merchants"
        />
      </div>

      {/* Pipeline + spotlight + queue live inside one card */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #E7ECF3",
          borderRadius: 18,
          padding: 18,
          boxShadow: "0 14px 40px rgba(16,24,40,0.08)",
        }}
      >
        {/* Pipeline */}
        <Pipeline
          stages={STAGES}
          counts={STAGES.reduce((acc, s) => ({ ...acc, [s.key]: stageCount(s.key) }), {})}
          selected={filter}
          pulse={pulseStage}
          onSelect={setFilter}
        />

        <div style={{ height: 1, background: "#E7ECF3", margin: "20px 0" }} />

        {/* Spotlight case */}
        {spotlight && (
          <SpotlightCase case_={spotlight} onAction={advance} />
        )}

        {/* Queue */}
        <div style={{ marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 950, color: "#0B1F33" }}>Queue</span>
              <span style={{ fontSize: 12, color: "#64748B", fontVariantNumeric: "tabular-nums" }}>
                · {filtered.length} in {STAGES.find((s) => s.key === filter)?.label.toLowerCase() || filter}
              </span>
            </div>
            {filter !== "review" && (
              <button
                type="button"
                onClick={() => setFilter("review")}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 12,
                  color: "#1D4ED8",
                  fontWeight: 900,
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: "inherit",
                }}
              >
                Reset to Officer review →
              </button>
            )}
          </div>
          <div style={{ border: "1px solid #E7ECF3", borderRadius: 12, overflow: "hidden" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#64748B", fontSize: 13 }}>
                No cases in this stage.
              </div>
            ) : (
              filtered.map((c, i) => (
                <QueueRow
                  key={c.id}
                  c={c}
                  selected={c.id === spotlightId}
                  onSelect={() => setSpotlightId(c.id)}
                  isFirst={i === 0}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

const StatCard = ({ label, value, sub }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #E7ECF3",
      borderRadius: 14,
      padding: 12,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
    }}
  >
    <div style={{ fontSize: 11, color: "#64748B", fontWeight: 700, letterSpacing: 0.02 }}>{label}</div>
    <div style={{ fontSize: 22, color: "#0B1F33", fontWeight: 950, marginTop: 4, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
      {value}
    </div>
    <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>{sub}</div>
  </div>
);


const Pipeline = ({ stages, counts, selected, pulse, onSelect }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${stages.length}, 1fr)`,
      alignItems: "flex-start",
      position: "relative",
    }}
  >
    {stages.map((st, i) => (
      <PipelineNode
        key={st.key}
        stage={st}
        count={counts[st.key] || 0}
        selected={selected === st.key}
        pulsing={pulse === st.key}
        onSelect={() => onSelect(st.key)}
        isLast={i === stages.length - 1}
      />
    ))}
  </div>
);

const PipelineNode = ({ stage, count, selected, pulsing, onSelect, isLast }) => {
  const { Icon, label, hitl } = stage;
  const nodeSize = hitl ? 56 : 44;
  const nodeBg = hitl
    ? "linear-gradient(135deg, #0B3A6E 0%, #1F63A8 100%)"
    : "linear-gradient(135deg, #1D4ED8 0%, #0B3E9B 100%)";
  const outlineColor = hitl ? "#0B3A6E" : "#1D4ED8";

  return (
    <div
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        cursor: "pointer",
      }}
    >
      {/* Connecting rail */}
      {!isLast && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: nodeSize / 2,
            left: `calc(50% + ${nodeSize / 2}px + 4px)`,
            right: `calc(-50% + ${nodeSize / 2}px + 4px)`,
            height: 2,
            borderRadius: 999,
            background: "linear-gradient(90deg, rgba(29,78,216,0.28) 0%, rgba(29,78,216,0.10) 40%, #E7ECF3 100%)",
            backgroundSize: "200% 100%",
            animation: "idz-rail-flow 3.6s linear infinite",
            zIndex: 0,
          }}
        />
      )}
      <div style={{ position: "relative", width: nodeSize, height: nodeSize, zIndex: 1 }}>
        {pulsing && (
          <span
            aria-hidden
            style={{
              position: "absolute",
              inset: -6,
              borderRadius: 999,
              background: `radial-gradient(circle, ${outlineColor}55 0%, transparent 70%)`,
              animation: "idz-stage-pulse 900ms ease-out forwards",
              pointerEvents: "none",
            }}
          />
        )}
        <div
          style={{
            position: "relative",
            width: nodeSize,
            height: nodeSize,
            borderRadius: 999,
            background: nodeBg,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: selected
              ? `0 0 0 4px ${outlineColor}33, 0 4px 12px -2px ${outlineColor}55`
              : hitl
                ? `0 0 0 3px ${outlineColor}22, 0 4px 10px -2px ${outlineColor}44`
                : `0 4px 10px -2px rgba(16,24,40,0.14)`,
            transition: "box-shadow 160ms ease, transform 200ms ease",
            transform: pulsing ? "scale(1.06)" : "scale(1)",
          }}
        >
          <Icon size={hitl ? 24 : 20} strokeWidth={1.75} />
        </div>
      </div>
      <div style={{ marginTop: 10, textAlign: "center", minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            color: hitl ? "#0B3A6E" : "#475569",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 20,
            fontWeight: 950,
            color: "#0B1F33",
            letterSpacing: "-0.02em",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {count}
        </div>
      </div>
    </div>
  );
};

const StageBadge = ({ stage }) => {
  const s = STAGE_STYLE[stage] || STAGE_STYLE.detected;
  return <Pill text={s.label.toUpperCase()} bg={s.bg} fg={s.fg} />;
};

const RiskBadge = ({ tier }) => {
  const s = RISK_STYLE[tier] || RISK_STYLE.Low;
  return <Pill text={`${tier} risk`.toUpperCase()} bg={s.bg} fg={s.fg} />;
};

const QueueRow = ({ c, selected, onSelect, isFirst }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "90px 1.4fr 130px 1fr 60px 90px 20px",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        cursor: "pointer",
        borderTop: isFirst ? "none" : "1px solid #F1F5F9",
        background: selected ? "#EFF8FF" : hover ? "#F8FAFF" : "transparent",
        boxShadow: selected ? "inset 2px 0 0 #1D4ED8" : "none",
        transition: "background 120ms ease",
      }}
    >
      <span style={{ fontSize: 12, color: "#64748B", fontWeight: 900, fontFamily: "'Geist Mono', 'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: "tabular-nums" }}>
        {c.id}
      </span>
      <span style={{ fontSize: 13, color: "#0B1F33", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {c.trigger}
      </span>
      <StageBadge stage={c.stage} />
      <span style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={c.entity}>
        {c.entity}
      </span>
      <span style={{ fontSize: 12, color: "#0B1F33", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
        {c.confidence}%
      </span>
      <span style={{ fontSize: 12, color: "#64748B", fontVariantNumeric: "tabular-nums" }}>
        {c.timeInStage}
      </span>
      <ChevronRight size={14} strokeWidth={1.75} color="#94A3B8" />
    </div>
  );
};

const SpotlightCase = ({ case_: c, onAction }) => {
  const inReview = c.stage === "review";
  return (
    <div style={{ display: "grid", gridTemplateColumns: inReview ? "1.4fr 1fr" : "1fr", gap: 20, alignItems: "start" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#64748B", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", fontFamily: "'Geist Mono', 'JetBrains Mono', ui-monospace, monospace" }}>
            {c.id}
          </span>
          <StageBadge stage={c.stage} />
          <RiskBadge tier={c.riskTier} />
          {c.fourEyes && <Pill text="4-EYES" bg="#EFF8FF" fg="#175CD3" />}
          {c.triggeredLever && (
            <Pill text={`GUARDRAIL · ${c.triggeredLever.toUpperCase()}`} bg="#EEF4FF" fg="#0B3E9B" />
          )}
          <span style={{ fontSize: 12, color: "#64748B", marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
            Confidence <strong style={{ color: "#0B1F33", fontWeight: 900 }}>{c.confidence}%</strong>
          </span>
        </div>

        <div style={{ fontSize: 18, fontWeight: 950, color: "#0B1F33", letterSpacing: "-0.01em", marginBottom: 4 }}>
          {c.trigger}
        </div>
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>
          <b>Entity:</b>{" "}<span style={{ color: "#475569" }}>{c.entity}</span>
          <span style={{ margin: "0 8px", color: "#94A3B8" }}>·</span>
          <b>Time in stage:</b>{" "}<span style={{ color: "#475569", fontVariantNumeric: "tabular-nums" }}>{c.timeInStage}</span>
        </div>

        <div style={{ fontSize: 11, color: "#64748B", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
          Evidence chain
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginBottom: 18 }}>
          {c.evidence.map((e, i) => (
            <div key={i} style={{ padding: "8px 10px", borderRadius: 8, background: "#F8FAFF", border: "1px solid #E7ECF3" }}>
              <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700 }}>{e.label}</div>
              <div style={{ fontSize: 13, color: "#0B1F33", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>{e.value}</div>
            </div>
          ))}
        </div>

        {/* Agent proposed action */}
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid #B3D0EC",
            background: "linear-gradient(180deg, rgba(29,78,216,0.06) 0%, rgba(29,78,216,0.02) 100%), #fff",
            marginBottom: 14,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#1D4ED8", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
            <Sparkles size={12} strokeWidth={2} />
            Agent proposed action
          </div>
          <div style={{ fontSize: 13, color: "#0B1F33", lineHeight: 1.5 }}>{c.proposedAction}</div>
        </div>

        {c.reasoning.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: "#64748B", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
              Chain of reasoning
            </div>
            <div style={{ padding: "10px 14px", borderRadius: 12, background: "#F8FAFF", border: "1px solid #E7ECF3", display: "flex", flexDirection: "column", gap: 6 }}>
              {c.reasoning.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: "'Geist Mono', 'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                    {r.t}
                  </span>
                  <span style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>{r.note}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {inReview && <OfficerReviewCard c={c} onAction={onAction} />}
    </div>
  );
};

const OfficerReviewCard = ({ c, onAction }) => {
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(null);
  const run = (mode) => {
    setProcessing(mode);
    setTimeout(() => {
      onAction(c.id, mode);
      setProcessing(null);
      setNote("");
    }, 400);
  };
  return (
    <div
      style={{
        padding: 18,
        borderRadius: 14,
        border: "1px solid #B3D0EC",
        background: "linear-gradient(180deg, rgba(29,78,216,0.08) 0%, rgba(29,78,216,0.02) 40%, #fff 100%)",
        boxShadow: "0 1px 3px rgba(29,78,216,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#1D4ED8", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
        <UserCheck size={12} strokeWidth={2} />
        Human-in-the-loop · Officer review
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, background: "#fff", border: "1px solid #E7ECF3", marginBottom: 14 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 999,
          background: "linear-gradient(135deg, #0B3A6E 0%, #1F63A8 100%)",
          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <UserCheck size={18} strokeWidth={1.75} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 950, color: "#0B1F33" }}>{c.officer.role}</div>
          <div style={{ fontSize: 11, color: "#64748B" }}>Assigned reviewer · queue rotation</div>
        </div>
      </div>

      <label style={{ display: "block", fontSize: 11, color: "#64748B", fontWeight: 700, marginBottom: 4 }}>
        Decision note (optional)
      </label>
      <textarea
        rows={2}
        placeholder="e.g. Ring confirmed via prior blocklist; approving hard-blacklist across sponsored merchants."
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: "100%",
          padding: "8px 10px",
          borderRadius: 8,
          border: "1px solid #E7ECF3",
          fontSize: 12,
          fontFamily: "inherit",
          color: "#0B1F33",
          background: "#fff",
          resize: "vertical",
          marginBottom: 12,
        }}
      />

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={() => run("approve")}
          disabled={processing !== null}
          style={{
            flex: 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34, border: "none", borderRadius: 8,
            background: "#0F7A3D", color: "#fff",
            fontSize: 13, fontWeight: 900,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "approve" ? 0.5 : 1,
            fontFamily: "inherit",
          }}
        >
          <Check size={14} strokeWidth={2.5} />
          {processing === "approve" ? "Approving…" : "Approve"}
        </button>
        <button
          type="button"
          onClick={() => run("reject")}
          disabled={processing !== null}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34, padding: "0 12px",
            border: "1px solid #E7ECF3", borderRadius: 8,
            background: "#fff", color: "#0B1F33",
            fontSize: 13, fontWeight: 700,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "reject" ? 0.5 : 1,
            fontFamily: "inherit",
          }}
        >
          <XIcon size={14} strokeWidth={2.5} />
          Reject
        </button>
        <button
          type="button"
          onClick={() => run("escalate")}
          disabled={processing !== null}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            height: 34, padding: "0 12px",
            border: "1px solid #E7ECF3", borderRadius: 8,
            background: "#fff", color: "#0B1F33",
            fontSize: 13, fontWeight: 700,
            cursor: processing ? "wait" : "pointer",
            opacity: processing && processing !== "escalate" ? 0.5 : 1,
            fontFamily: "inherit",
          }}
        >
          <ArrowUpRight size={14} strokeWidth={2.5} />
          Escalate
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: "#64748B", lineHeight: 1.5 }}>
        {c.fourEyes
          ? "4-eyes required — a second officer must co-sign hard-blacklists affecting more than 3 users."
          : "Auto-approval eligible — this action is within the agent's autonomy envelope."}
      </div>

      {/* Learning-loop message — makes it explicit that the officer's
          decision doesn't just resolve the case, it tunes the model. */}
      <div
        style={{
          marginTop: 10,
          padding: "8px 10px",
          borderRadius: 8,
          background: "#EFF4FF",
          border: "1px solid #DBE4F3",
          fontSize: 11,
          color: "#0B3E9B",
          lineHeight: 1.5,
        }}
      >
        <b>Learning loop —</b>{" "}
        {processing === "approve"
          ? "↑ Confidence in this pattern reinforced. Similar cases will surface faster and may auto-action at your current threshold."
          : processing === "reject"
          ? "↓ Confidence in this pattern reduced. Similar signals will require additional evidence before routing."
          : c.triggeredLever
          ? `Your decision here retunes the ${c.triggeredLever.toLowerCase()} weight for ring-signal patterns. Model retrains after every 4-eyes call.`
          : "Your decision here retunes the agent's confidence in this pattern. Model retrains after every officer call."}
      </div>
    </div>
  );
};

const Pill = ({ text, tone, bg, fg }) => {
  const toneMap = {
    info:    { bg: "#EFF8FF", fg: "#175CD3" },
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good:    { bg: "#ECFDF3", fg: "#027A48" },
    warn:    { bg: "#FFFAEB", fg: "#B54708" },
    bad:     { bg: "#FEF3F2", fg: "#B42318" },
  };
  const c = tone ? toneMap[tone] : { bg, fg };
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: "0.06em",
        padding: "3px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};
